import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUpRight, Sparkles, Zap } from "lucide-react";
import { HomeToolCard } from "@/components/site/HomeToolCard";
import { FavoriteButton } from "@/features/favorites/FavoriteButton";
import { useFavorites } from "@/features/favorites/use-favorites";
import { i18nLinks, i18nOgMeta } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { tools } from "@/tools";
import { getToolsByCategory } from "@/tools/categories";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Toolverse — Your Command Center for Specialized Web Tools" },
      {
        name: "description",
        content:
          "Toolverse is a curated collection of precision-built web tools. Each tool solves a specific problem exceptionally well. Built for performance.",
      },
      {
        property: "og:title",
        content: "Toolverse — Your Command Center for Specialized Web Tools",
      },
      {
        property: "og:description",
        content:
          "Precision-built web tools for every specialized problem. Each tool crafted with obsessive attention to detail.",
      },
      ...i18nOgMeta("/"),
    ],
    links: [...i18nLinks("/")],
  }),
  component: HomePage,
});

/**
 * Home page: "Aurora" design
 * Atmospheric elegance. Aurora gradient backdrop.
 * Floating logo with glow halo. Frosted glass cards with gradient borders.
 * All theming handled via CSS (dark: variants) — no JS theme state.
 */
function HomePage() {
  const { signedIn, favorites } = useFavorites();
  const favoriteTools = favorites
    .map((id) => tools.find((t) => t.id === id))
    .filter((t): t is (typeof tools)[number] => t != null);

  return (
    <div className="flex-1 bg-background text-foreground">
      {/* Hero */}
      <section className="relative py-12 md:py-16 lg:py-20 flex items-center justify-center overflow-hidden">
        {/* Aurora gradient — dark: deep atmospheric, light: subtle color wash */}
        <div className="absolute inset-0 dark:hidden hero-aurora-light" />
        <div className="absolute inset-0 hidden dark:block hero-aurora" />
        {/* Orbs — dark only */}
        <div className="absolute top-[15%] left-[20%] w-64 h-64 rounded-full blur-[80px] animate-pulse hidden dark:block orb-blue" />
        <div className="absolute bottom-[20%] right-[15%] w-48 h-48 rounded-full blur-[60px] animate-pulse [animation-delay:3s] hidden dark:block orb-pink" />
        {/* Noise texture — dark only */}
        <div
          className="absolute inset-0 opacity-[0.015] hidden dark:block"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="relative px-5 text-center max-w-5xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-4 opacity-0 animate-slide-up">
            <div className="relative animate-float">
              <div className="w-40 h-40 md:w-64 md:h-64 rounded-[1rem] md:rounded-[1.5rem] flex items-center justify-center">
                <img
                  src="/toolverse-icon.svg"
                  alt="Toolverse"
                  width="256"
                  height="256"
                  fetchPriority="high"
                  className="w-32 h-32 md:w-52 md:h-52 object-contain logo-glow"
                />
              </div>
              {/* Logo halo — colored glow behind logo */}
              <div className="absolute -inset-3 rounded-[1.5rem] blur-xl -z-10 logo-halo-light dark:logo-halo" />
              <div className="absolute -inset-6 rounded-[2.5rem] bg-primary/[0.03] blur-2xl -z-20 dark:bg-primary/[0.05]" />
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-4 opacity-0 animate-slide-up [animation-delay:100ms] bg-card border-border badge-elevated dark:bg-card/50 dark:backdrop-blur-xl">
            <Sparkles className="h-3 w-3 text-primary" />
            <span className="text-sm font-semibold tracking-wide text-muted-foreground">
              {m.home_badge()}
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.92] mb-4 opacity-[0.01] animate-slide-up [animation-delay:200ms]">
            <span className="block mb-1">{m.home_hero_line1()}</span>
            <span className="block gradient-text-aurora">
              {m.home_hero_line2()}
            </span>
          </h1>

          <p className="text-sm md:text-base max-w-md mx-auto leading-relaxed mb-6 opacity-0 animate-slide-up [animation-delay:300ms] text-muted-foreground">
            {m.home_hero_subtitle()}
          </p>

          {/* CTA — mobile only (desktop shows tools without scrolling) */}
          <div className="flex items-center justify-center opacity-0 animate-slide-up [animation-delay:400ms] md:hidden">
            <a
              href="#tools"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 hover:scale-[1.03] active:scale-[0.98]"
            >
              {m.home_hero_cta_explore()}
              <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>

        {/* Fade to content */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Tool Showcase */}
      <section id="tools" className="px-5 py-6 md:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-3">
              <Zap className="h-3 w-3 text-primary" />
              <span className="text-[11px] font-semibold text-primary tracking-wide">
                {m.home_tools_available_now()}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight gradient-text-aurora">
              {m.home_tools_title()}
            </h2>
          </div>

          <div className="space-y-10">
            {signedIn && favoriteTools.length > 0 && (
              <div id="favorites" className="scroll-mt-24">
                <div className="mb-4 flex items-baseline gap-2">
                  <span className="text-xl" aria-hidden>
                    ⭐
                  </span>
                  <h3 className="text-lg md:text-xl font-bold tracking-tight">
                    {m.home_favorites_title()}
                  </h3>
                  <span className="text-xs font-medium text-muted-foreground">
                    {favoriteTools.length}
                  </span>
                </div>
                <div className="space-y-3">
                  {favoriteTools.map((tool) => (
                    <HomeToolCard
                      key={tool.id}
                      tool={tool}
                      action={<FavoriteButton toolId={tool.id} />}
                    />
                  ))}
                </div>
              </div>
            )}

            {getToolsByCategory().map(({ category, tools: catTools }) => (
              <div key={category.id} id={category.id} className="scroll-mt-24">
                <div className="mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl" aria-hidden>
                      {category.icon}
                    </span>
                    <h3 className="text-lg md:text-xl font-bold tracking-tight">
                      {category.name()}
                    </h3>
                    <span className="text-xs font-medium text-muted-foreground">
                      {catTools.length}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {category.description()}
                  </p>
                </div>
                <div className="space-y-3">
                  {catTools.map((tool) => (
                    <HomeToolCard
                      key={tool.id}
                      tool={tool}
                      action={<FavoriteButton toolId={tool.id} />}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / SEO content — real on-page text so the homepage is not a
          bare app shell (AdSense/search quality). All copy via Paraglide. */}
      <section className="px-5 py-10 md:py-14">
        <div className="max-w-3xl mx-auto space-y-10">
          <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              {m.home_seo_title()}
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              {m.home_seo_p1()}
            </p>
            <p className="leading-relaxed text-muted-foreground">
              {m.home_seo_p2()}
            </p>
          </div>

          {(
            [
              {
                title: m.home_seo_f1_title(),
                body: [m.home_seo_f1_p1(), m.home_seo_f1_p2()],
                to: "/f1-fantasy",
                cta: m.home_seo_f1_cta(),
              },
              {
                title: m.home_seo_pct_title(),
                body: [m.home_seo_pct_p1()],
                to: "/percentage-calculator",
                cta: m.home_seo_pct_cta(),
              },
              {
                title: m.home_seo_timer_title(),
                body: [m.home_seo_timer_p1()],
                to: "/timer",
                cta: m.home_seo_timer_cta(),
              },
              {
                title: m.home_seo_yt_title(),
                body: [m.home_seo_yt_p1()],
                to: "/youtube-thumbnail-downloader",
                cta: m.home_seo_yt_cta(),
              },
              {
                title: m.home_seo_typing_title(),
                body: [m.home_seo_typing_p1()],
                to: "/typing-speed-test",
                cta: m.home_seo_typing_cta(),
              },
              {
                title: m.home_seo_wordunscrambler_title(),
                body: [m.home_seo_wordunscrambler_p1()],
                to: "/word-unscrambler",
                cta: m.home_seo_wordunscrambler_cta(),
              },
              {
                title: m.home_seo_sip_title(),
                body: [m.home_seo_sip_p1()],
                to: "/sip-calculator",
                cta: m.home_seo_sip_cta(),
              },
              {
                title: m.home_seo_signature_title(),
                body: [m.home_seo_signature_p1()],
                to: "/signature-maker",
                cta: m.home_seo_signature_cta(),
              },
              {
                title: m.home_seo_unitconverter_title(),
                body: [m.home_seo_unitconverter_p1()],
                to: "/unit-converter",
                cta: m.home_seo_unitconverter_cta(),
              },
              {
                title: m.home_seo_age_title(),
                body: [m.home_seo_age_p1()],
                to: "/age-calculator",
                cta: m.home_seo_age_cta(),
              },
              {
                title: m.home_seo_bmi_title(),
                body: [m.home_seo_bmi_p1()],
                to: "/bmi-calculator",
                cta: m.home_seo_bmi_cta(),
              },
              {
                title: m.home_seo_pw_title(),
                body: [m.home_seo_pw_p1()],
                to: "/password-generator",
                cta: m.home_seo_pw_cta(),
              },
              {
                title: m.home_seo_dd_title(),
                body: [m.home_seo_dd_p1()],
                to: "/date-difference",
                cta: m.home_seo_dd_cta(),
              },
              {
                title: m.home_seo_rg_title(),
                body: [m.home_seo_rg_p1()],
                to: "/random-generator",
                cta: m.home_seo_rg_cta(),
              },
              {
                title: m.home_seo_wc_title(),
                body: [m.home_seo_wc_p1()],
                to: "/word-counter",
                cta: m.home_seo_wc_cta(),
              },
              {
                title: m.home_seo_cc_title(),
                body: [m.home_seo_cc_p1()],
                to: "/case-converter",
                cta: m.home_seo_cc_cta(),
              },
              {
                title: m.home_seo_jf_title(),
                body: [m.home_seo_jf_p1()],
                to: "/json-formatter",
                cta: m.home_seo_jf_cta(),
              },
              {
                title: m.home_seo_li_title(),
                body: [m.home_seo_li_p1()],
                to: "/lorem-ipsum",
                cta: m.home_seo_li_cta(),
              },
              {
                title: m.home_seo_b64_title(),
                body: [m.home_seo_b64_p1()],
                to: "/base64",
                cta: m.home_seo_b64_cta(),
              },
              {
                title: m.home_seo_ue_title(),
                body: [m.home_seo_ue_p1()],
                to: "/url-encode",
                cta: m.home_seo_ue_cta(),
              },
              {
                title: m.home_seo_cv_title(),
                body: [m.home_seo_cv_p1()],
                to: "/color-converter",
                cta: m.home_seo_cv_cta(),
              },
              {
                title: m.home_seo_hg_title(),
                body: [m.home_seo_hg_p1()],
                to: "/hash-generator",
                cta: m.home_seo_hg_cta(),
              },
              {
                title: m.home_seo_td_title(),
                body: [m.home_seo_td_p1()],
                to: "/text-diff",
                cta: m.home_seo_td_cta(),
              },
              {
                title: m.home_seo_nw_title(),
                body: [m.home_seo_nw_p1()],
                to: "/number-to-words",
                cta: m.home_seo_nw_cta(),
              },
              {
                title: m.home_seo_sg_title(),
                body: [m.home_seo_sg_p1()],
                to: "/slug-generator",
                cta: m.home_seo_sg_cta(),
              },
              {
                title: m.home_seo_rd_title(),
                body: [m.home_seo_rd_p1()],
                to: "/remove-duplicate-lines",
                cta: m.home_seo_rd_cta(),
              },
              {
                title: m.home_seo_tr_title(),
                body: [m.home_seo_tr_p1()],
                to: "/text-repeat",
                cta: m.home_seo_tr_cta(),
              },
              {
                title: m.home_seo_wf_title(),
                body: [m.home_seo_wf_p1()],
                to: "/word-frequency",
                cta: m.home_seo_wf_cta(),
              },
              {
                title: m.home_seo_ls_title(),
                body: [m.home_seo_ls_p1()],
                to: "/line-sorter",
                cta: m.home_seo_ls_cta(),
              },
              {
                title: m.home_seo_rv_title(),
                body: [m.home_seo_rv_p1()],
                to: "/text-reverse",
                cta: m.home_seo_rv_cta(),
              },
              {
                title: m.home_seo_wsc_title(),
                body: [m.home_seo_wsc_p1()],
                to: "/whitespace-cleaner",
                cta: m.home_seo_wsc_cta(),
              },
              {
                title: m.home_seo_fr_title(),
                body: [m.home_seo_fr_p1()],
                to: "/find-replace",
                cta: m.home_seo_fr_cta(),
              },
              {
                title: m.home_seo_tb_title(),
                body: [m.home_seo_tb_p1()],
                to: "/text-to-binary",
                cta: m.home_seo_tb_cta(),
              },
              {
                title: m.home_seo_mc_title(),
                body: [m.home_seo_mc_p1()],
                to: "/morse-code",
                cta: m.home_seo_mc_cta(),
              },
              {
                title: m.home_seo_rn_title(),
                body: [m.home_seo_rn_p1()],
                to: "/roman-numeral",
                cta: m.home_seo_rn_cta(),
              },
              {
                title: m.home_seo_ci_title(),
                body: [m.home_seo_ci_p1()],
                to: "/char-inspector",
                cta: m.home_seo_ci_cta(),
              },
              {
                title: m.home_seo_nb_title(),
                body: [m.home_seo_nb_p1()],
                to: "/number-base",
                cta: m.home_seo_nb_cta(),
              },
              {
                title: m.home_seo_ds_title(),
                body: [m.home_seo_ds_p1()],
                to: "/data-size",
                cta: m.home_seo_ds_cta(),
              },
              {
                title: m.home_seo_ar_title(),
                body: [m.home_seo_ar_p1()],
                to: "/aspect-ratio",
                cta: m.home_seo_ar_cta(),
              },
              {
                title: m.home_seo_ln_title(),
                body: [m.home_seo_ln_p1()],
                to: "/loan-calculator",
                cta: m.home_seo_ln_cta(),
              },
              {
                title: m.home_seo_cp_title(),
                body: [m.home_seo_cp_p1()],
                to: "/compound-interest",
                cta: m.home_seo_cp_cta(),
              },
              {
                title: m.home_seo_cj_title(),
                body: [m.home_seo_cj_p1()],
                to: "/csv-to-json",
                cta: m.home_seo_cj_cta(),
              },
              {
                title: m.home_seo_cr_title(),
                body: [m.home_seo_cr_p1()],
                to: "/cron-expression-explainer",
                cta: m.home_seo_cr_cta(),
              },
              {
                title: m.home_seo_mt_title(),
                body: [m.home_seo_mt_p1()],
                to: "/markdown-table-generator",
                cta: m.home_seo_mt_cta(),
              },
              {
                title: m.home_seo_jw_title(),
                body: [m.home_seo_jw_p1()],
                to: "/jwt-decoder",
                cta: m.home_seo_jw_cta(),
              },
              {
                title: m.home_seo_ra_title(),
                body: [m.home_seo_ra_p1()],
                to: "/readability-checker",
                cta: m.home_seo_ra_cta(),
              },
              {
                title: m.home_seo_ud_title(),
                body: [m.home_seo_ud_p1()],
                to: "/uuid-inspector",
                cta: m.home_seo_ud_cta(),
              },
              {
                title: m.home_seo_blog_title(),
                body: [m.home_seo_blog_p1()],
                to: "/blog",
                cta: m.home_seo_blog_cta(),
              },
              {
                title: m.home_seo_syllable_title(),
                body: [m.home_seo_syllable_p1()],
                to: "/syllable-counter",
                cta: m.home_seo_syllable_cta(),
              },
            ] as const
          ).map((s) => (
            <div key={s.to} className="space-y-2">
              <h3 className="text-lg font-semibold tracking-tight">
                {s.title}
              </h3>
              {s.body.map((p) => (
                <p key={p} className="leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
              <Link
                to={s.to}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                {s.cta}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}

          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              {m.home_faq_title()}
            </h2>
            <dl className="space-y-3">
              {[
                { q: m.home_faq1_q(), a: m.home_faq1_a() },
                { q: m.home_faq2_q(), a: m.home_faq2_a() },
                { q: m.home_faq3_q(), a: m.home_faq3_a() },
                { q: m.home_faq4_q(), a: m.home_faq4_a() },
              ].map((f) => (
                <div
                  key={f.q}
                  className="rounded-xl border border-border bg-card/40 p-4"
                >
                  <dt className="font-semibold text-foreground">{f.q}</dt>
                  <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </div>
  );
}
